import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { resetPassword } from '@/api/auth';
import { Mail, ArrowLeft, CheckCircle2 } from 'lucide-react';

export function ResetRoute() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);
    try {
      await resetPassword(token, password);
      setDone(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err: any) {
      setError(err.message || 'Reset failed — the link may be invalid or expired');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-bmail-navy px-4">
      <div className="w-full max-w-sm rounded-2xl bg-bmail-surface p-6 shadow-xl border border-bmail-border">
        <div className="mb-8 flex items-center justify-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-bmail-goldfill text-bmail-on-accent">
            <Mail size={28} strokeWidth={2.5} />
          </div>
          <h1 className="text-3xl font-bold text-bmail-text tracking-tight">
            <span className="text-bmail-gold">B-</span>Mail
          </h1>
        </div>

        {done ? (
          <div className="text-center space-y-4">
            <CheckCircle2 className="mx-auto h-16 w-16 text-bmail-gold" />
            <h2 className="text-xl font-semibold text-bmail-text">Password updated</h2>
            <p className="text-sm text-bmail-text">
              You'll need to re-enter passwords for your mailboxes after signing in.
            </p>
            <p className="text-xs text-bmail-text-secondary">Redirecting to login…</p>
          </div>
        ) : (
          <>
            <h2 className="mb-2 text-center text-xl font-semibold text-bmail-text">Set a new password</h2>
            <p className="mb-6 text-center text-sm text-bmail-text-secondary">
              Choose a new password for your B-Mail account.
            </p>

            {error && (
              <div className="mb-4 rounded-lg bg-bmail-error/10 px-4 py-3 text-sm text-bmail-error">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-bmail-text">New password</label>
                <input
                  type="password"
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-lg border border-bmail-border bg-bmail-navy px-4 py-2.5 text-bmail-text placeholder-slate-500 focus:border-bmail-gold focus:outline-none"
                  placeholder="••••••••"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-bmail-text">Confirm password</label>
                <input
                  type="password"
                  required
                  minLength={8}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="w-full rounded-lg border border-bmail-border bg-bmail-navy px-4 py-2.5 text-bmail-text placeholder-slate-500 focus:border-bmail-gold focus:outline-none"
                  placeholder="••••••••"
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full rounded-lg bg-bmail-goldfill px-4 py-3 font-semibold text-bmail-on-accent hover:bg-bmail-goldfill-hover disabled:opacity-50 transition-colors"
              >
                {isLoading ? 'Updating…' : 'Update password'}
              </button>
            </form>

            <div className="mt-6 text-center">
              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-sm text-bmail-text-secondary hover:text-bmail-gold transition-colors"
              >
                <ArrowLeft size={16} /> Back to sign in
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}